/**
 * slideContentNormalizer.js
 * Converts raw backend slide content into UI-safe editable state per archetype.
 * Never mutates the original slide object.
 */

// ─── Default empty schemas per archetype ────────────────────────────────────
const EMPTY_DEFAULTS = {
    bullets: [''],
    table: { column_headers: [], content: [{}] },
    timeline: [{ weeks: '', title: '', description: '' }],
    chevron: [{ stage: '', action: '' }],
    journey: [{ top_text: '', bottom_text: '' }],
    detailed_journey: [{ top_label: '', header: '', content: '' }],
    funnel: [{ stage: '', description: '' }],
    labeled_funnel: [{ stage: '', description: '' }],
    cycle: { center_text: '', content: [''] },
    radial_hub: { center_text: '', content: [''] },
    matrix: { center_text: '', content: { top_left: '', top_right: '', bottom_left: '', bottom_right: '' } },
    detailed_matrix: {
        center_text: '',
        content: {
            top_left: { header: '', content: '' },
            top_right: { header: '', content: '' },
            bottom_left: { header: '', content: '' },
            bottom_right: { header: '', content: '' },
        }
    },
    venn: [{ header: '', content: '' }],
    pyramid: [{ header: '', content: '' }],
    spotlight: [{ headline: '', subtext: '' }],
    before_after: {
        before: { header: '', content: [''] },
        after: { header: '', content: [''] },
    },
    case_study: [{ client: '', obstacle: '', solution: '', result: '' }],
    qa: [{ question: '', answer: '' }],
    big_number: { summary: '', content: [{ number: '', label: '' }] },
    chart_sidebar: {
        chart_heading: '', takeaway_heading: '', takeaway_content: '',
        chart_data: { categories: [], series: [] }
    },
    section_divider: { headline: '' },
};

// ─── Helpers ────────────────────────────────────────────────────────────────
const tryParseJson = (raw) => {
    if (typeof raw !== 'string') return raw;
    const t = raw.trim();
    if ((t.startsWith('{') && t.endsWith('}')) || (t.startsWith('[') && t.endsWith(']'))) {
        try { return JSON.parse(t); } catch { /* ignore */ }
    }
    return raw;
};

const toArray = (val) => (Array.isArray(val) ? val : val != null ? [val] : []);

const ensureStringArray = (val) => {
    if (!val || (Array.isArray(val) && val.length === 0)) return [''];
    return toArray(val).map(item => {
        if (typeof item === 'string') return item;
        if (typeof item === 'object') return JSON.stringify(item);
        return String(item);
    });
};

// ─── Per-archetype normalizers ───────────────────────────────────────────────
const normalizers = {
    bullets(content) {
        if (!content || (Array.isArray(content) && content.length === 0)) return [''];
        return ensureStringArray(content);
    },

    table(content, columnHeaders) {
        const parsed = tryParseJson(content);
        // Backend may wrap in { column_headers, content }
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed) && parsed.content) {
            const hdrs = columnHeaders || parsed.column_headers || Object.keys((parsed.content[0]) || {});
            return { column_headers: hdrs, content: toArray(parsed.content) };
        }
        if (Array.isArray(parsed) && parsed.length > 0) {
            const hdrs = columnHeaders || Object.keys(parsed[0] || {});
            return { column_headers: hdrs, content: parsed };
        }
        return EMPTY_DEFAULTS.table;
    },

    timeline(content) {
        const arr = toArray(tryParseJson(content));
        if (!arr.length) return EMPTY_DEFAULTS.timeline;
        return arr.map(item => ({
            weeks: item.weeks || item.week || '',
            title: item.title || item.headline || '',
            description: item.description || item.subtext || '',
        }));
    },

    chevron(content) {
        const arr = toArray(tryParseJson(content));
        if (!arr.length) return EMPTY_DEFAULTS.chevron;
        // Support nested format { title, content: [{ stage, action }] }
        if (arr[0] && typeof arr[0] === 'object' && 'content' in arr[0] && Array.isArray(arr[0].content)) {
            // Flatten nested format
            return arr.flatMap(group => toArray(group.content).map(item => ({
                stage: item.stage || group.title || '',
                action: item.action || '',
            })));
        }
        return arr.map(item => ({
            stage: item.stage || item.headline || '',
            action: item.action || item.subtext || '',
        }));
    },

    journey(content) {
        const arr = toArray(tryParseJson(content));
        if (!arr.length) return EMPTY_DEFAULTS.journey;
        return arr.map(item => ({
            top_text: item.top_text || item.headline || '',
            bottom_text: item.bottom_text || item.subtext || '',
        }));
    },

    detailed_journey(content) {
        const arr = toArray(tryParseJson(content));
        if (!arr.length) return EMPTY_DEFAULTS.detailed_journey;
        return arr.map(item => ({
            top_label: item.top_label || '',
            header: item.header || item.headline || '',
            content: typeof item.content === 'string' ? item.content : (item.subtext || ''),
        }));
    },

    funnel(content) {
        const arr = toArray(tryParseJson(content));
        if (!arr.length) return EMPTY_DEFAULTS.funnel;
        return arr.map(item => ({
            stage: item.stage || item.headline || '',
            description: item.description || item.subtext || '',
        }));
    },

    labeled_funnel(content) {
        return normalizers.funnel(content);
    },

    cycle(content) {
        const parsed = tryParseJson(content);
        const arr = Array.isArray(parsed) ? parsed : null;
        if (arr && arr[0] && typeof arr[0] === 'object' && ('center_text' in arr[0] || 'content' in arr[0])) {
            const item = arr[0];
            return { center_text: item.center_text || '', content: ensureStringArray(item.content) };
        }
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
            return { center_text: parsed.center_text || '', content: ensureStringArray(parsed.content) };
        }
        return EMPTY_DEFAULTS.cycle;
    },

    radial_hub(content) {
        return normalizers.cycle(content);
    },

    matrix(content) {
        const parsed = tryParseJson(content);
        const arr = Array.isArray(parsed) ? parsed : null;
        const obj = arr ? arr[0] : parsed;
        if (obj && typeof obj === 'object') {
            const hasDirectKeys = 'top_left' in obj || 'top_right' in obj || 'bottom_left' in obj || 'bottom_right' in obj;
            const c = hasDirectKeys ? obj : (obj.content || {});
            return {
                center_text: obj.center_text || '',
                content: {
                    top_left: typeof c.top_left === 'string' ? c.top_left : (c.top_left?.content || ''),
                    top_right: typeof c.top_right === 'string' ? c.top_right : (c.top_right?.content || ''),
                    bottom_left: typeof c.bottom_left === 'string' ? c.bottom_left : (c.bottom_left?.content || ''),
                    bottom_right: typeof c.bottom_right === 'string' ? c.bottom_right : (c.bottom_right?.content || ''),
                }
            };
        }
        return EMPTY_DEFAULTS.matrix;
    },

    detailed_matrix(content) {
        const parsed = tryParseJson(content);
        const arr = Array.isArray(parsed) ? parsed : null;
        const obj = arr ? arr[0] : parsed;
        if (obj && typeof obj === 'object') {
            const hasDirectKeys = 'top_left' in obj || 'top_right' in obj || 'bottom_left' in obj || 'bottom_right' in obj;
            const c = hasDirectKeys ? obj : (obj.content || {});
            const toQ = (v) => typeof v === 'object' ? { header: v.header || '', content: v.content || '' } : { header: '', content: String(v || '') };
            return {
                center_text: obj.center_text || '',
                content: {
                    top_left: toQ(c.top_left),
                    top_right: toQ(c.top_right),
                    bottom_left: toQ(c.bottom_left),
                    bottom_right: toQ(c.bottom_right),
                }
            };
        }
        return EMPTY_DEFAULTS.detailed_matrix;
    },

    venn(content) {
        const arr = toArray(tryParseJson(content));
        if (!arr.length) return EMPTY_DEFAULTS.venn;
        return arr.map(item => ({
            header: item.header || item.headline || '',
            content: typeof item.content === 'string' ? item.content : (item.subtext || ''),
        }));
    },

    pyramid(content) {
        return normalizers.venn(content);
    },

    spotlight(content) {
        const arr = toArray(tryParseJson(content));
        if (!arr.length) return EMPTY_DEFAULTS.spotlight;
        return arr.map(item => ({
            headline: item.headline || item.header || '',
            subtext: item.subtext || (typeof item.content === 'string' ? item.content : ''),
        }));
    },

    before_after(content) {
        const parsed = tryParseJson(content);
        const obj = Array.isArray(parsed) ? parsed[0] : parsed;
        if (obj && typeof obj === 'object' && ('before' in obj || 'after' in obj)) {
            return {
                before: { header: obj.before?.header || '', content: ensureStringArray(obj.before?.content) },
                after: { header: obj.after?.header || '', content: ensureStringArray(obj.after?.content) },
            };
        }
        return EMPTY_DEFAULTS.before_after;
    },

    case_study(content) {
        const arr = toArray(tryParseJson(content));
        if (!arr.length) return EMPTY_DEFAULTS.case_study;
        return arr.map(item => ({
            client: item.client || '',
            obstacle: item.obstacle || item.challenge || '',
            solution: item.solution || '',
            result: item.result || item.outcome || '',
        }));
    },

    qa(content) {
        const arr = toArray(tryParseJson(content));
        if (!arr.length) return EMPTY_DEFAULTS.qa;
        return arr.map(item => ({
            question: item.question || item.headline || '',
            answer: item.answer || item.subtext || '',
        }));
    },

    big_number(content) {
        const parsed = tryParseJson(content);
        if (!parsed) return EMPTY_DEFAULTS.big_number;

        // Case 1: parsed is an Array of number/label items
        if (Array.isArray(parsed)) {
            const first = parsed[0];
            if (first && typeof first === 'object' && ('number' in first || 'label' in first || 'value' in first)) {
                return {
                    summary: '',
                    content: parsed.map(c => ({
                        number: c.number ?? c.value ?? '',
                        label: c.label ?? c.title ?? ''
                    }))
                };
            }
            return {
                summary: '',
                content: parsed.map(c => {
                    if (typeof c === 'object') {
                        return { number: c.number || c.value || '', label: c.label || c.title || '' };
                    }
                    return { number: '', label: String(c || '') };
                })
            };
        }

        // Case 2: parsed is an Object
        if (typeof parsed === 'object') {
            if ('content' in parsed && Array.isArray(parsed.content)) {
                return {
                    summary: parsed.summary || '',
                    content: parsed.content.map(c => ({
                        number: c.number ?? c.value ?? '',
                        label: c.label ?? c.title ?? ''
                    }))
                };
            }
            if ('number' in parsed || 'label' in parsed || 'value' in parsed) {
                return {
                    summary: '',
                    content: [{
                        number: parsed.number ?? parsed.value ?? '',
                        label: parsed.label ?? parsed.title ?? ''
                    }]
                };
            }
        }

        return EMPTY_DEFAULTS.big_number;
    },

    chart_sidebar(content) {
        const parsed = tryParseJson(content);
        const obj = Array.isArray(parsed) ? parsed[0] : parsed;
        if (obj && typeof obj === 'object') {
            return {
                chart_heading: obj.chart_heading || '',
                takeaway_heading: obj.takeaway_heading || '',
                takeaway_content: obj.takeaway_content || '',
                chart_data: obj.chart_data || { categories: [], series: [] },
            };
        }
        return EMPTY_DEFAULTS.chart_sidebar;
    },

    section_divider(content) {
        const arr = Array.isArray(content) ? content : null;
        if (arr && arr.length > 0 && typeof arr[0] === 'string') {
            return { headline: arr[0] };
        }
        const obj = tryParseJson(content);
        if (obj && typeof obj === 'object' && obj.headline) return { headline: obj.headline };
        if (typeof content === 'string') return { headline: content };
        return EMPTY_DEFAULTS.section_divider;
    },

    auto(content, columnHeaders) {
        const parsed = tryParseJson(content);
        if (Array.isArray(parsed) && parsed.length > 0) {
            if (typeof parsed[0] === 'object') {
                return normalizers.table(content, columnHeaders);
            }
            return normalizers.bullets(content);
        }
        if (parsed && typeof parsed === 'object' && parsed.content) {
             return normalizers.table(content, columnHeaders);
        }
        return normalizers.bullets(content);
    }
};

// ─── Validation ──────────────────────────────────────────────────────────────
export const validateSlideContent = (archetype, content) => {
    const errors = [];
    try {
        if (archetype === 'auto') {
            // If it normalized as a table, check table rules, else bullets
            if (content && content.column_headers) archetype = 'table';
            else archetype = 'bullets';
        }
        switch (archetype) {
            case 'bullets':
                if (!Array.isArray(content)) errors.push('Content must be an array of strings.');
                break;
            case 'table':
                if (!content?.column_headers?.length) errors.push('Table must have column headers.');
                if (!Array.isArray(content?.content)) errors.push('Table content must be an array of rows.');
                break;
            case 'chevron':
                if (!Array.isArray(content)) errors.push('Chevron must be an array.');
                content?.forEach((item, i) => {
                    if (!item.stage) errors.push(`Row ${i + 1}: "stage" is required.`);
                    if (!item.action) errors.push(`Row ${i + 1}: "action" is required.`);
                });
                break;
            case 'timeline':
                if (!Array.isArray(content)) errors.push('Timeline must be an array.');
                content?.forEach((item, i) => {
                    if (!item.title) errors.push(`Row ${i + 1}: "title" is required.`);
                });
                break;
            case 'matrix':
                if (!content?.content) errors.push('Matrix must have a content object.');
                else {
                    ['top_left', 'top_right', 'bottom_left', 'bottom_right'].forEach(k => {
                        if (!content.content[k] && content.content[k] !== '') errors.push(`Matrix missing key: "${k}".`);
                    });
                }
                break;
            case 'qa':
                if (!Array.isArray(content)) errors.push('Q&A must be an array.');
                content?.forEach((item, i) => {
                    if (!item.question) errors.push(`Row ${i + 1}: "question" is required.`);
                });
                break;
            default:
                break;
        }
    } catch (e) {
        errors.push(`Validation error: ${e.message}`);
    }
    return errors;
};

// ─── Main export ─────────────────────────────────────────────────────────────
export const normalizeSlideContent = (slide) => {
    const archetype = slide?.archetype || slide?.layout || 'bullets';
    const raw = slide?.content;
    const columnHeaders = slide?.column_headers || null;

    const normalizer = normalizers[archetype];
    if (normalizer) {
        try {
            return normalizer(raw, columnHeaders);
        } catch (e) {
            console.warn(`[normalizeSlideContent] Failed for archetype "${archetype}":`, e);
            return EMPTY_DEFAULTS[archetype] ?? raw ?? '';
        }
    }
    // Unknown archetype – return raw content as-is for generic JSON editor
    return raw ?? '';
};

export const EMPTY_DEFAULTS_MAP = EMPTY_DEFAULTS;
